import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  DocumentCropSettings,
  ImageDocumentStoreItem,
  ODataDocumentCropSettings,
  ODataOrRetrievedDocumentReference,
  RetrievedDocumentReferenceCropSettings,
} from '../../../../components/v2/components/imageHandling/types/types';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { AppDispatch } from '@store/types';
import { scale as initialScale } from '@components/frame';
import { limitMinimumSize, smallImageHeight } from '@components/imageHandling/common';
import { generateImageURL } from '@reducers/blobStorage';
import { shouldSwapDimensions } from '@v2/components/imageHandling/helpers';

// HELPERS

const swapDimensions = (document: ImageDocumentStoreItem): void => {
  const tempWidth = document.widthOriginal;
  document.widthOriginal = document.heightOriginal;
  document.heightOriginal = tempWidth;
};

const buildCustomRotationMap = (parsedDocument: ImageDocumentStoreItem): Record<number, number> => {
  const customRotate: Record<number, number> = {};
  const baseDetails = parsedDocument?.pageDetails?.find((detail) => detail.pageNumber === parsedDocument.pageNumber) || { rotationAngle: 0 };

  // Set initial rotation for current page
  customRotate[parsedDocument.pageNumber!] = parsedDocument.isCropped ? 0 : baseDetails.rotationAngle ?? 0;

  // Process all page details for PDF
  if (parsedDocument.pageDetails) {
    parsedDocument.pageDetails.forEach((detail) => {
      const crop = parsedDocument.cropSettings?.find((c) => c.pageNumber === detail.pageNumber);

      // If page wasn't cropped, we need to use rotationAngle from pageDetails and rotate it locally. Otherwise - rotation already had being done by backend
      if (!crop || crop.rotationAngle === undefined) {
        customRotate[detail.pageNumber!] = detail.rotationAngle ?? 0;
      }
    });
  }

  return customRotate;
};

export const calculateCropDimensions = ({
  height,
  cropSettings = [],
  pageNumber = 1,
  width,
  customAngle,
}: {
  cropSettings: ODataDocumentCropSettings[] | RetrievedDocumentReferenceCropSettings[];
  pageNumber: number;
  width: number;
  height: number;
  customAngle?: number;
}): { width: number; height: number; x: number; y: number } => {
  const c = cropSettings?.find((s) => s.pageNumber === pageNumber);

  if (!c) {
    return { width, height, x: 0, y: 0 };
  }

  const angle = customAngle ?? c.rotationAngle ?? 0;

  if (angle === 0 || angle === 180) {
    return {
      width: Math.round(width * (c.lowerRightXPosition! - c.upperLeftXPosition!)),
      height: Math.round(height * (c.lowerRightYPosition! - c.upperLeftYPosition!)),
      x: Math.round(c.upperLeftXPosition! * width),
      y: Math.round(c.upperLeftYPosition! * height),
    };
  } else {
    return {
      width: Math.round(height * (c.lowerRightXPosition! - c.upperLeftXPosition!)),
      height: Math.round(width * (c.lowerRightYPosition! - c.upperLeftYPosition!)),
      x: Math.round(c.upperLeftXPosition! * height),
      y: Math.round(c.upperLeftYPosition! * width),
    };
  }
};

export const buildUniqueParamsForImageUrl = ({
  lowerRightXPosition,
  lowerRightYPosition,
  rotationAngle,
  upperLeftXPosition,
  upperLeftYPosition,
}: DocumentCropSettings): string => {
  const params = new URLSearchParams();

  if (upperLeftXPosition != null) {
    params.append('crop_x1', upperLeftXPosition.toFixed(6));
  }
  if (upperLeftYPosition != null) {
    params.append('crop_y1', upperLeftYPosition.toFixed(6));
  }
  if (lowerRightXPosition != null) {
    params.append('crop_x2', lowerRightXPosition.toFixed(6));
  }
  if (lowerRightYPosition != null) {
    params.append('crop_y2', lowerRightYPosition.toFixed(6));
  }
  if (rotationAngle != null) {
    params.append('rotation', rotationAngle.toString());
  }

  return params.toString();
};

export const generatePageUrls = async ({
  dispatch,
  cropSettings,
  imageDocument,
  pageNumber,
}: {
  pageNumber: number;
  imageDocument: ODataOrRetrievedDocumentReference;
  cropSettings: DocumentCropSettings[];
  dispatch: AppDispatch;
}) => {
  const cropped = cropSettings?.some((c) => c.pageNumber === pageNumber) ?? false;
  const cropSetting = cropSettings?.find((c) => c.pageNumber === pageNumber);

  const baseParams = {
    isPDF: true,
    page: pageNumber,
    item: imageDocument,
  };

  // Add unique params for cropped images to prevent caching issues
  const additionalParams = cropped && cropSetting ? buildUniqueParamsForImageUrl(cropSetting) : undefined;

  return {
    url: await dispatch(generateImageURL({ size: '_3x', cropped, additionalParams, ...baseParams })),
    urlZoom: await dispatch(generateImageURL({ size: '_large', cropped, additionalParams, ...baseParams })),
    urlSmall: await dispatch(generateImageURL({ size: '_1x', cropped, additionalParams, ...baseParams })),
    originUrl3x: await dispatch(generateImageURL({ size: '_3x', cropped: false, ...baseParams })),
    originUrl: await dispatch(generateImageURL({ size: '_large', cropped: false, ...baseParams })),
  };
};

export const generateImageUrls = async (parsedDocument: ImageDocumentStoreItem, dispatch: AppDispatch) => {
  // Get crop settings for current page to build unique params
  const cropSetting = parsedDocument?.cropSettings?.find((c) => c.pageNumber === parsedDocument.pageNumber);
  const additionalParams = parsedDocument.isCropped && cropSetting ? buildUniqueParamsForImageUrl(cropSetting) : undefined;

  return {
    url: await dispatch(
      generateImageURL({
        size: '_1x',
        cropped: parsedDocument.isCropped,
        additionalParams,
        item: parsedDocument,
      })
    ),
    urlZoom: await dispatch(
      generateImageURL({
        size: '_large',
        cropped: parsedDocument.isCropped,
        additionalParams,
        item: parsedDocument,
      })
    ),
    urlSmall: await dispatch(
      generateImageURL({
        size: '_1x',
        cropped: parsedDocument.isCropped,
        additionalParams,
        item: parsedDocument,
      })
    ),
    originUrl: await dispatch(
      generateImageURL({
        size: '_large',
        cropped: false,
        item: parsedDocument,
      })
    ),
    originUrl3x: await dispatch(
      generateImageURL({
        size: '_3x',
        cropped: false,
        item: parsedDocument,
      })
    ),
  };
};

const processPDFDocument = async ({
  customRotate,
  dispatch,
  imageDocument,
  parsedDocument,
}: {
  parsedDocument: ImageDocumentStoreItem;
  imageDocument: ODataOrRetrievedDocumentReference;
  customRotate: Record<number, number>;
  dispatch: AppDispatch;
}) => {
  const pagesList: Record<number, any> = {};
  const pageCount = imageDocument.pageCount ?? 0;

  // Generate URLs for each page
  for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
    pagesList[pageNum] = await generatePageUrls({
      pageNumber: pageNum,
      imageDocument,
      cropSettings: parsedDocument.cropSettings ?? [],
      dispatch,
    });
  }

  // Adjust dimensions if current page is rotated
  if (shouldSwapDimensions(customRotate[parsedDocument.pageNumber!])) {
    swapDimensions(parsedDocument);
  }

  const cropDimensions = calculateCropDimensions({
    pageNumber: parsedDocument.pageNumber!,
    cropSettings: parsedDocument.cropSettings ?? [],
    width: parsedDocument.widthOriginal,
    height: parsedDocument.heightOriginal,
  });

  return {
    ...parsedDocument,
    customRotate,
    pagesList,
    ...pagesList[parsedDocument.pageNumber!],
    ...cropDimensions,
    ...cropDimensions,
  };
};

const processRegularImage = async ({
  customRotate,
  dispatch,
  parsedDocument,
}: {
  parsedDocument: ImageDocumentStoreItem;
  customRotate: Record<number, number>;
  dispatch: AppDispatch;
}) => {
  // Adjust dimensions for non-cropped rotated images
  if (!parsedDocument.isCropped && shouldSwapDimensions(customRotate[parsedDocument.pageNumber!])) {
    swapDimensions(parsedDocument);
  }

  const urls = await generateImageUrls(parsedDocument, dispatch);
  const cropDimensions = calculateCropDimensions({
    cropSettings: parsedDocument.cropSettings ?? [],
    pageNumber: parsedDocument.pageNumber!,
    width: parsedDocument.widthOriginal,
    height: parsedDocument.heightOriginal,
  });

  return {
    ...urls,
    ...parsedDocument,
    ...cropDimensions,
    customRotate,
  };
};

// MAIN FUNCTION
export const parseAndAddUrlsToImagesDocuments = createAsyncThunk<
  ImageDocumentStoreItem[],
  { imagesDocuments: ODataOrRetrievedDocumentReference[]; scale?: ScaleFunction },
  { dispatch: AppDispatch }
>('imageHandlingV2/parseAndAddUrlsToImagesDocuments', async ({ imagesDocuments, scale = initialScale }, { dispatch }) => {
  const processedDocuments: ImageDocumentStoreItem[] = [];

  for (const imageDocument of imagesDocuments) {
    if (!imageDocument) {
      continue;
    }

    // Extract basic document properties
    const isPDF = imageDocument.extensionOriginal === 'pdf';
    const isDocument = imageDocument.documentType === 'Other';
    const docTag = imageDocument.tags?.[0];
    const documentTag = docTag != null ? (typeof docTag === 'string' ? docTag : docTag.name) : '';

    // Create parsed document with initial properties
    const parsedImageDocument: ImageDocumentStoreItem = {
      ...imageDocument,
      customRotate: {},
      id: imageDocument.documentId!,
      widthOriginal: 0,
      heightOriginal: 0,
      activeImageWidth: 0,
      documentTag,
      isPDF,
      isDocument,
      downloadUrl: '',
      pagesList: {},
    };

    // Get document details and dimensions
    const details = imageDocument?.pageDetails;

    if (details?.[0]) {
      parsedImageDocument.widthOriginal = details[0].width ?? 0;
      parsedImageDocument.heightOriginal = details[0].height ?? 0;
    }

    // Calculate active image width
    const activeImageWidth = limitMinimumSize(
      (scale(smallImageHeight) * parsedImageDocument.widthOriginal) / parsedImageDocument.heightOriginal,
      scale
    );
    parsedImageDocument.activeImageWidth = activeImageWidth;

    // Generate download URL
    parsedImageDocument.downloadUrl = await dispatch(
      generateImageURL({
        item: parsedImageDocument,
        download: true,
      })
    );

    // Check if document is cropped, because isCropped field represents whether whole document was cropped. But we need to know whether exact page was cropped
    parsedImageDocument.isCropped = parsedImageDocument.cropSettings?.some((c) => c.pageNumber === parsedImageDocument.pageNumber!) ?? false;

    // Build rotation map
    const customRotate = buildCustomRotationMap(parsedImageDocument);

    // Process based on document type
    const processedDocument = isPDF
      ? await processPDFDocument({ parsedDocument: parsedImageDocument, imageDocument, customRotate, dispatch })
      : await processRegularImage({ parsedDocument: parsedImageDocument, customRotate, dispatch });

    processedDocuments.push(processedDocument);
  }

  return processedDocuments;
});
